import { ReactNode, useState, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireActiveSubscription?: boolean;
}

export function ProtectedRoute({ 
  children, 
  requireAdmin = false,
  requireActiveSubscription = false 
}: ProtectedRouteProps) {
  const { user, loading, isAdmin, profile, adminCheckComplete } = useAuth();
  const location = useLocation();
  const [profileTimeout, setProfileTimeout] = useState(false);
  const verifiedUserRef = useRef<string | null>(null); // Track verified user

  // CRITICAL: Timeout failsafe - if profile doesn't load in 2 seconds, proceed anyway (reduced from 3s)
  // This prevents infinite loading if profile fetch fails after a publish/deploy
  useEffect(() => {
    if (user && !profile && requireActiveSubscription && !profileTimeout) {
      const timer = setTimeout(() => {
        console.warn('[ProtectedRoute] Profile load timeout after 2s, proceeding with redirect');
        setProfileTimeout(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, profile, requireActiveSubscription, profileTimeout]);

  // Once verified, remember the user to prevent flicker on navigation
  useEffect(() => {
    if (adminCheckComplete && user) {
      verifiedUserRef.current = user.id;
    }
  }, [adminCheckComplete, user]);

  // Wait for both auth AND admin check to complete
  if (loading || !adminCheckComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // CRITICAL: Admins bypass ALL checks immediately - no waiting for profile or subscription
  if (isAdmin) {
    console.log('[ProtectedRoute] Admin detected, bypassing all checks');
    return <>{children}</>;
  }

  // If user exists but profile hasn't loaded yet for subscription check, wait (WITH TIMEOUT)
  if (user && !profile && requireActiveSubscription && !profileTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check for active subscription (admins bypass this check entirely)
  if (requireActiveSubscription) {
    const membershipStatus = profile?.membership_status;
    const membershipPlan = profile?.membership_plan;
    
    // Admins bypass subscription check completely (check both role and plan)
    // isAdmin comes from user_roles table, membershipPlan === 'admin' is a fallback for manual admin accounts
    console.log('[ProtectedRoute] Admin check - isAdmin:', isAdmin, 'membershipPlan:', membershipPlan);
    if (isAdmin || membershipPlan === 'admin') {
      return <>{children}</>;
    }
    
    // For regular users, require active subscription status with a paid plan
    const paidPlans = ['student', 'mastery', 'admin'];
    const hasActiveSubscription = membershipStatus === 'active' && 
      membershipPlan && 
      paidPlans.includes(membershipPlan);
    
    if (!hasActiveSubscription) {
      return <Navigate to="/subscription-required" replace />;
    }
  }

  return <>{children}</>;
}
